/**
 * Project Service
 *
 * Handles all project-related operations including CRUD operations,
 * filtering, searching, and project status management.
 *
 * @module services/projectService
 */

import { supabase } from './supabase';
import { terminoParaFiltro, columnaNormalizada } from '../utils/normalizacionBusqueda';
import { PROJECT_STATUS, BUDGET_TRACKED_PROJECT_STATUSES, excludePersonalProjects } from '../utils/constants';
import { arrayToCSV } from '../utils/exportHelpers';
// #686 — el aviso estructurado de datos de prueba, escrito una sola vez.
import { avisoDatosDePrueba, contarOcultas } from './testDataScope';
import { resolveInitialProjectStatus } from './projectStatusResolver';
// #765 — los umbrales 80/100, nombrados una sola vez en el árbol web.
import { BUDGET_WARNING_THRESHOLD, BUDGET_CRITICAL_THRESHOLD } from '../utils/budgetStatus';
import { createDefaultMilestones } from './milestoneService';
// L10 — el comprometido cuenta también la línea de compra que hereda el
// proyecto del encabezado. Gemelo del carril MCP en
// `mcp-server/src/services/purchaseProjectTag.js`.
import { fetchOpenCommittedLines, committedAmountByProject } from '../utils/purchaseProjectTag';

/**
 * Calculate budget usage variance/percentage/status from base amounts.
 *
 * Single source of truth for the (expenses + committed) / quoted math used
 * across the project budget summary (getProject), the budget alerts list
 * (getProjectsWithBudgetAlerts), the dashboard widget (BudgetOverviewWidget),
 * and the Historial de Proyectos table (ProjectsTable) -- see #276/#325/#406.
 * Do not reimplement this math elsewhere.
 *
 * #679/#685 — SIN PRESUPUESTO NO HAY PORCENTAJE NI VEREDICTO.
 *
 * Esto devolvía `percentageUsed: 0` y `status: 'ok'` cuando no había monto
 * cotizado, o sea el mismo defecto que las tools MCP: el número que emite el
 * veredicto no miraba el gasto, y "todavía nadie cotizó" salía pintado de
 * verde "Normal" en la tabla de proyectos y en el widget del dashboard.
 * Ahora usa el mismo vocabulario que `calculateBudgetVariance`
 * (`src/utils/calculations.js`) y que la base (`check_project_budget_status`):
 * `no_budget`, con los derivados en `null`.
 *
 * @param {number|null} quotedAmount
 * @param {number} totalExpenses
 * @param {number} committedAmount
 * @returns {{variance: number|null, percentageUsed: number|null,
 *   status: 'no_budget'|'ok'|'warning'|'critical'}}
 */
export const calculateBudgetMetrics = (quotedAmount = 0, totalExpenses = 0, committedAmount = 0) => {
  const consumed = (totalExpenses || 0) + (committedAmount || 0);

  if (!quotedAmount || quotedAmount <= 0) {
    return {
      variance: null,
      percentageUsed: null,
      status: 'no_budget',
    };
  }

  const variance = quotedAmount - consumed;
  const percentageUsed = (consumed / quotedAmount) * 100;
  const status = percentageUsed >= BUDGET_CRITICAL_THRESHOLD
    ? 'critical'
    : percentageUsed >= BUDGET_WARNING_THRESHOLD ? 'warning' : 'ok';

  return {
    variance,
    percentageUsed: Math.round(percentageUsed * 100) / 100,
    status,
  };
};

/**
 * Get aggregated expense + committed-PO totals for a batch of projects using
 * two queries total, regardless of how many project ids are passed in --
 * avoids the N+1 pattern of querying per-row (#406). Mirrors the
 * committed-amount calculation used by getProject/getProjectBudgetStatus so
 * every consumer of this helper agrees with the MCP agent's
 * check_budget_status (#276).
 *
 * @param {string[]} projectIds
 * @returns {Promise<Object<string, {totalExpenses: number, committedAmount: number}>>}
 */
export const getBudgetTotalsByProject = async (projectIds) => {
  const ids = [...new Set((projectIds || []).filter(Boolean))];
  const totals = {};
  ids.forEach((id) => {
    totals[id] = { totalExpenses: 0, committedAmount: 0 };
  });

  if (ids.length === 0) return totals;

  const { data: expenses, error: expensesError } = await supabase
    .from('expenses')
    .select('project_id, amount')
    .in('project_id', ids);

  if (expensesError) throw expensesError;

  (expenses || []).forEach((exp) => {
    if (totals[exp.project_id]) {
      totals[exp.project_id].totalExpenses += exp.amount || 0;
    }
  });

  // Committed amount: open purchase order lines (approved/sent/partial),
  // mirroring the MCP agent's check_budget_status calculation (#276).
  //
  // L10 — se resuelve el proyecto EFECTIVO (línea > encabezado). Una línea sin
  // `project_id` cuyo encabezado sí apunta al proyecto no es una compra de
  // bodega: es una línea mal creada, y hasta L10 lo eran casi todas (cuatro de
  // los cinco sitios de INSERT omitían la columna). Saltearla reportaba un
  // comprometido de 0 sobre una obligación real.
  const openLines = await fetchOpenCommittedLines(supabase, ids);
  const committed = committedAmountByProject(openLines);
  committed.forEach((amount, projectId) => {
    if (totals[projectId]) totals[projectId].committedAmount += amount;
  });

  return totals;
};

/**
 * Get all projects with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Project status
 * @param {string} filters.clientId - Client ID
 * @param {string} filters.search - Search term
 * @param {number} filters.page - Page number (0-indexed)
 * @param {number} filters.pageSize - Items per page
 * @param {boolean} filters.includeTestData - #360: incluir proyectos marcados
 *   `is_test_data` (fixtures de tests/E2E). Por defecto se excluyen, igual que
 *   en la tool `list_projects` del carril MCP.
 * @returns {Promise<{data, count, error}>}
 */
export const listProjects = async (filters = {}) => {
  try {
    // #A4: el embed de `project_leaders` se agrega SÓLO cuando hay filtro por
    // responsable. Es `!inner`, o sea que acota la lista — pedirlo siempre
    // dejaría afuera todo proyecto sin líder asignado (hoy, todos).
    const leaderEmbed = filters.assigned_to
      ? ',\n        project_leaders!inner(user_id)'
      : '';

    let query = supabase
      .from('projects')
      .select(`
        *,
        client:clients!projects_client_id_fkey(id, company_name, primary_contact_name, primary_contact_phone),
        created_by_profile:profiles!projects_created_by_fkey(id, full_name)${leaderEmbed}
      `, { count: 'exact' });

    // #405: never list per-user personal-tasks containers here.
    query = excludePersonalProjects(query);

    // #360 (paridad de carriles): el carril MCP ya excluía los proyectos de
    // prueba por defecto (`mcp-server/src/tools/projectTools.js` — list_projects
    // con include_test_data=false), pero el carril web los seguía listando como
    // reales. Mismo default en los dos: se ocultan salvo pedido explícito.
    // Hoy es un no-op sobre prod (0 de 71 proyectos marcados), así que no
    // cambia lo que Jorge ve; empieza a valer en cuanto se marque algo.
    if (!filters.includeTestData) {
      query = query.eq('is_test_data', false);
    }

    // Apply filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.clientId) {
      query = query.eq('client_id', filters.clientId);
    }

    if (filters.search) {
      // #742/#739 — el termino sale del modulo compartido: normaliza tildes,
      // saca los metacaracteres de PostgREST (`,` `(` `)` son sintaxis dentro de
      // un `.or()`) y escapa los comodines de LIKE. `title` se compara contra su
      // columna generada `title_norm`; `project_number` se queda crudo porque es
      // un codigo, no texto humano — no tiene tildes que normalizar y el ILIKE ya
      // es insensible a mayusculas.
      const termino = terminoParaFiltro(filters.search);
      query = query.or(`project_number.ilike.%${termino}%,${columnaNormalizada('title')}.ilike.%${termino}%`);
    }

    // #A4: "Responsable" y "Desde"/"Hasta" existían en FilterBar.js, contaban
    // para el badge azul de filtros activos... y morían acá: esta función sólo
    // leía status/clientId/search. El botón "Filtros" se ponía en "3" y la
    // tabla mostraba exactamente los mismos proyectos.
    //
    // "Responsable" NO es una columna de `projects` (verificado contra prod:
    // no hay `assigned_to`). La única relación real de responsabilidad es la
    // tabla `project_leaders` (project_id, user_id), que es la que llena
    // ProjectLeadersPanel. Se filtra por ahí, con un embed !inner para que el
    // join acote de verdad. Hoy `project_leaders` está vacía en prod, así que
    // filtrar por responsable devuelve 0 — y eso es la VERDAD (ningún proyecto
    // tiene líder asignado todavía), no un estado vacío que miente. Empieza a
    // dar resultados en cuanto se asigne el primero.
    if (filters.assigned_to) {
      query = query.eq('project_leaders.user_id', filters.assigned_to);
    }

    // Rango sobre `created_at`. `date_to` viene de un <input type="date">
    // (medianoche), así que se extiende al final del día: si no, "hasta hoy"
    // dejaba afuera todo lo creado hoy.
    if (filters.date_from) {
      query = query.gte('created_at', new Date(`${filters.date_from}T00:00:00`).toISOString());
    }
    if (filters.date_to) {
      query = query.lte('created_at', new Date(`${filters.date_to}T23:59:59.999`).toISOString());
    }

    // Apply pagination
    const page = filters.page || 0;
    const pageSize = filters.pageSize || 20;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    query = query
      .range(from, to)
      .order('created_at', { ascending: false });

    const { data, count, error } = await query;

    if (error) throw error;

    // #686 — EL SILENCIO ERA LA MITAD DEL BUG, Y ACÁ TAMBIÉN ESTABA. Este
    // carril filtra desde #360 y devolvía `{ data, count }` sin decir que había
    // sacado nada: una búsqueda por número exacto contestaba "sin resultados"
    // sobre un proyecto que existe, igual que del lado del agente. El contador
    // mide el MISMO universo con el filtro invertido.
    let testDataFilter = avisoDatosDePrueba({
      include: Boolean(filters.includeTestData), ocultas: 0, sujeto: 'proyectos',
    });
    if (!filters.includeTestData) {
      const ocultas = await contarOcultas(() => {
        // Mismo orden de hops que el query de arriba, para que el contador mida
        // el MISMO universo: select -> excludePersonalProjects -> is_test_data.
        let c = excludePersonalProjects(
          supabase.from('projects').select('id', { count: 'exact', head: true })
        ).eq('is_test_data', true);
        if (filters.status) c = c.eq('status', filters.status);
        if (filters.clientId) c = c.eq('client_id', filters.clientId);
        if (filters.search) {
          // EL MISMO filtro que la pagina, o el contador de #686 mide otro universo.
          const termino = terminoParaFiltro(filters.search);
          c = c.or(`project_number.ilike.%${termino}%,${columnaNormalizada('title')}.ilike.%${termino}%`);
        }
        return c;
      });
      testDataFilter = avisoDatosDePrueba({ include: false, ocultas, sujeto: 'proyectos' });
    }

    return { data, count, testDataFilter, error: null };
  } catch (error) {
    console.error('List projects error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get project by ID with all related data
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
export const getProject = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        client:clients!projects_client_id_fkey(id, company_name, primary_contact_name, primary_contact_phone),
        created_by_profile:profiles!projects_created_by_fkey(id, full_name),
        quotations!quotations_project_id_fkey(id, quotation_number, status, total, created_at),
        expenses(id, amount, category, expense_date, vendor)
      `)
      .eq('id', projectId)
      .single();

    if (error) throw error;

    // Committed amount: open purchase order lines (approved/sent/partial),
    // mirroring the MCP agent's check_budget_status calculation so this
    // page's budget summary matches what the agent reports (#276).
    // L10: proyecto EFECTIVO (línea > encabezado) — ver getBudgetTotalsByProject.
    const committedAmount = committedAmountByProject(
      await fetchOpenCommittedLines(supabase, [projectId])
    ).get(projectId) || 0;

    // Calculate budget summary. #469: this used to re-derive the percentage
    // and the 80/100 thresholds inline, a third copy of the math that
    // `calculateBudgetMetrics` above already declares itself the single
    // source of truth for. Delegating keeps the no-budget guard (#467) and
    // the thresholds in exactly one place per lane.
    // #679: el monto contratado viaja FIEL. `null` es "nadie decidió todavía";
    // colapsarlo a 0 acá era lo que hacía que la tarjeta de presupuesto dijera
    // "restante: -30.000" sobre un proyecto que no tiene presupuesto.
    const quotedAmount = data.quoted_amount ?? null;
    const totalExpenses = data.expenses?.reduce((sum, exp) => sum + (exp.amount || 0), 0) || 0;
    const { variance, percentageUsed, status } = calculateBudgetMetrics(
      quotedAmount,
      totalExpenses,
      committedAmount
    );

    return {
      data: {
        ...data,
        budgetSummary: {
          quotedAmount,
          totalExpenses,
          committedAmount,
          variance,
          variancePercentage: percentageUsed,
          status,
        },
      },
      error: null,
    };
  } catch (error) {
    console.error('Get project error:', error);
    return { data: null, error };
  }
};

/**
 * Create new project
 * @param {Object} projectData - Project data
 * @param {string} projectData.title - Project title
 * @param {string} projectData.description - Project description
 * @param {string} projectData.client_id - Client ID (optional)
 * @param {string} projectData.estimated_payment_date - Estimated payment date (optional)
 * @param {Object} [options] - Creation options
 * @param {boolean} [options.seedDefaultMilestones=true] - Auto-create the
 *   6-stage default milestone template (#460: Cotización → Planeación →
 *   Compras → Ejecución → Evaluación → Cierre). Set to false to create a
 *   project with zero milestones (e.g. bulk imports, tests).
 * @returns {Promise<{data, error}>}
 */
export const createProject = async (projectData, options = {}) => {
  try {
    const { seedDefaultMilestones = true } = options;

    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // #353: never assert a commercial state we did not earn. Resolved the
    // same way as the MCP tool's createProject (mcp-server/src/tools/
    // projectTools.js) via resolveInitialProjectStatus() — see
    // projectStatusResolver.js for the full root cause. Issue #296's
    // "both creation paths agree on quotation_sent" is still true today
    // (nothing changes until REACT_APP_PROJECT_INITIAL_STATUS=draft + the
    // migration land), but it is no longer asserted as fact.
    const initialStatus = resolveInitialProjectStatus();

    // #360: marcar como dato de prueba es EXPLÍCITO. Antes esto llegaba por
    // el spread de `projectData` y era truthiness pura; ahora sólo el `true`
    // literal marca, igual que en los carriles MCP. Un `true` de más deja un
    // proyecto REAL fuera del listado (que filtra is_test_data=false) y
    // elegible para la próxima limpieza autorizada — por eso se falla hacia
    // "no marcar". Nunca se deriva de NODE_ENV: ver la cabecera de
    // supabase/migrations/20260727190000_clients_is_test_data.sql.
    const { is_test_data, ...restProjectData } = projectData || {};
    const markAsTest = is_test_data === true;

    const { data, error } = await supabase
      .from('projects')
      .insert({
        ...restProjectData,
        ...(markAsTest ? { is_test_data: true } : {}),
        created_by: user.id,
        status: initialStatus.status,
      })
      .select(`
        *,
        client:clients!projects_client_id_fkey(id, company_name, primary_contact_name),
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    // #460: seed the fixed 6-milestone checklist right after the project
    // exists. Best-effort — a seeding failure must not undo project
    // creation (the project is fully usable with zero milestones, same as
    // before this feature existed); it surfaces as a non-fatal warning
    // instead.
    let milestonesWarning = null;
    if (seedDefaultMilestones && data?.id) {
      const milestonesResult = await createDefaultMilestones(data.id);
      if (milestonesResult.error) {
        console.error('Failed to seed default milestones for project', data.id, milestonesResult.error);
        milestonesWarning = 'El proyecto se creó, pero no se pudieron generar los hitos por defecto.';
      }
    }

    return {
      data,
      error: null,
      status_provisional: initialStatus.provisional,
      ...(initialStatus.warning ? { warning: initialStatus.warning } : {}),
      ...(milestonesWarning ? { milestones_warning: milestonesWarning } : {}),
    };
  } catch (error) {
    console.error('Create project error:', error);
    return { data: null, error };
  }
};

/**
 * Update project
 * @param {string} projectId - Project ID
 * @param {Object} updates - Project updates
 * @returns {Promise<{data, error}>}
 */
export const updateProject = async (projectId, updates) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', projectId)
      .select(`
        *,
        client:clients!projects_client_id_fkey(id, company_name, primary_contact_name),
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update project error:', error);
    return { data: null, error };
  }
};

/**
 * Update project status
 * @param {string} projectId - Project ID
 * @param {string} status - New status
 * @returns {Promise<{data, error}>}
 */
export const updateProjectStatus = async (projectId, status) => {
  return updateProject(projectId, { status });
};

/**
 * Update project PO number
 * @param {string} projectId - Project ID
 * @param {string} poNumber - PO number
 * @returns {Promise<{data, error}>}
 */
export const updateProjectPO = async (projectId, poNumber) => {
  return updateProject(projectId, { po_number: poNumber });
};

/**
 * Delete project (admin only)
 *
 * El `.select()` NO es cosmético: es lo único que distingue "borré" de "no
 * borré".
 *
 * Un DELETE de PostgREST sin `.select()` responde `204 No Content` con
 * `error: null` incluso cuando RLS no dejó tocar ninguna fila (RLS no rechaza:
 * filtra, así que el DELETE afecta 0 filas y eso es un éxito para Postgres).
 * Solo el admin tiene política DELETE sobre `projects`, así que para supervisor
 * y técnico el borrado no ocurría nunca — y la UI, mirando `error === null`,
 * decía "Proyecto eliminado exitosamente". El proyecto seguía ahí al recargar.
 *
 * Con `.select()` la respuesta trae las filas efectivamente borradas: si viene
 * vacía, no se borró nada y hay que decirlo.
 *
 * @param {string} projectId - Project ID
 * @returns {Promise<{success, error}>}
 */
export const deleteProject = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId)
      .select('id');

    if (error) throw error;

    if (!data || data.length === 0) {
      throw new Error(
        'No se eliminó el proyecto: no existe o no tienes permiso para eliminarlo ' +
        '(solo un administrador puede borrar proyectos).'
      );
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete project error:', error);
    return { success: false, error };
  }
};

/**
 * Get project budget status
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
export const getProjectBudgetStatus = async (projectId) => {
  try {
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('quoted_amount')
      .eq('id', projectId)
      .single();

    if (projectError) throw projectError;

    const { data: expenses, error: expensesError } = await supabase
      .from('expenses')
      .select('amount')
      .eq('project_id', projectId);

    if (expensesError) throw expensesError;

    // Committed amount: open purchase order lines (approved/sent/partial),
    // mirroring the MCP agent's check_budget_status calculation (#276).
    // L10: proyecto EFECTIVO (línea > encabezado) — ver getBudgetTotalsByProject.
    const committedAmount = committedAmountByProject(
      await fetchOpenCommittedLines(supabase, [projectId])
    ).get(projectId) || 0;

    // #679: fiel, igual que en getProject.
    const quotedAmount = project.quoted_amount ?? null;
    const totalExpenses = expenses?.reduce((sum, exp) => sum + (exp.amount || 0), 0) || 0;

    // #469: same consolidation as getProject above — the percentage and the
    // 80/100 thresholds live in `calculateBudgetMetrics`, not inline here.
    const { variance, percentageUsed, status } = calculateBudgetMetrics(
      quotedAmount,
      totalExpenses,
      committedAmount
    );

    return {
      data: {
        quotedAmount,
        totalExpenses,
        committedAmount,
        variance,
        variancePercentage: percentageUsed,
        status,
      },
      error: null,
    };
  } catch (error) {
    console.error('Get project budget status error:', error);
    return { data: null, error };
  }
};

/**
 * Get projects with budget alerts
 * @param {number} threshold - Alert threshold percentage (default BUDGET_WARNING_THRESHOLD, #765)
 * @returns {Promise<{data, error}>}
 */
export const getProjectsWithBudgetAlerts = async (threshold = BUDGET_WARNING_THRESHOLD) => {
  try {
    // Get all budget-tracked projects. Uses the same status set as the
    // "Resumen de Presupuestos" widget (BudgetOverviewWidget) so the alert
    // counter and the table it summarizes never disagree (issue #261).
    const { data: projects, error: projectsError } = await excludePersonalProjects(
      supabase
        .from('projects')
        .select(`
          id,
          project_number,
          title,
          quoted_amount,
          status
        `)
        .in('status', BUDGET_TRACKED_PROJECT_STATUSES)
    );

    if (projectsError) throw projectsError;

    // Get expense + committed-PO totals for all these projects in two
    // queries total (not per-project) via the shared batched helper (#406).
    const projectIds = projects.map(p => p.id);
    const totalsByProject = await getBudgetTotalsByProject(projectIds);

    // Calculate budget status for each project using the shared metrics helper
    const projectsWithAlerts = projects
      .map(project => {
        const { totalExpenses, committedAmount } = totalsByProject[project.id] || {
          totalExpenses: 0,
          committedAmount: 0,
        };
        const { percentageUsed, status } = calculateBudgetMetrics(
          project.quoted_amount ?? null,
          totalExpenses,
          committedAmount
        );

        return {
          ...project,
          totalExpenses,
          committedAmount,
          variancePercentage: percentageUsed,
          status,
        };
      })
      .filter(p => p.variancePercentage >= threshold)
      .sort((a, b) => b.variancePercentage - a.variancePercentage);

    return { data: projectsWithAlerts, error: null };
  } catch (error) {
    console.error('Get projects with budget alerts error:', error);
    return { data: null, error };
  }
};

/**
 * Get project summary for dashboard
 * @returns {Promise<{data, error}>}
 */
export const getProjectsSummary = async () => {
  try {
    // #405: exclude personal-tasks containers so they don't inflate the
    // quotation_sent count / totals on the dashboard summary.
    const { data, error } = await excludePersonalProjects(
      supabase
        .from('projects')
        .select('id, status, quoted_amount')
    );

    if (error) throw error;

    const summary = {
      total: data.length,
      byStatus: {
        quotation_sent: data.filter(p => p.status === PROJECT_STATUS.QUOTATION_SENT).length,
        quotation_accepted: data.filter(p => p.status === PROJECT_STATUS.QUOTATION_ACCEPTED).length,
        po_received: data.filter(p => p.status === PROJECT_STATUS.PO_RECEIVED).length,
        in_progress: data.filter(p => p.status === PROJECT_STATUS.IN_PROGRESS).length,
        closed: data.filter(p => p.status === PROJECT_STATUS.CLOSED).length,
      },
      totalValue: data.reduce((sum, p) => sum + (p.quoted_amount || 0), 0),
    };

    return { data: summary, error: null };
  } catch (error) {
    console.error('Get projects summary error:', error);
    return { data: null, error };
  }
};

/**
 * Export projects to CSV
 * @param {Array} projects - Array of projects
 * @returns {string} CSV string
 */
export const exportProjectsToCSV = (projects) => {
  const headers = [
    { key: 'project_number', label: 'Número' },
    { key: 'title', label: 'Título' },
    { key: 'client', label: 'Cliente' },
    { key: 'status', label: 'Estado' },
    { key: 'quoted_amount', label: 'Monto Cotizado' },
    { key: 'po_number', label: 'PO' },
    { key: 'created_at', label: 'Fecha Creación' },
  ];

  const rows = projects.map(project => ({
    project_number: project.project_number,
    title: project.title,
    client: project.client?.company_name || project.client?.primary_contact_name || '-',
    status: project.status,
    quoted_amount: project.quoted_amount || '-',
    po_number: project.po_number || '-',
    created_at: new Date(project.created_at).toLocaleDateString('es-CR'),
  }));

  return arrayToCSV(rows, headers);
};

const projectService = {
  listProjects,
  getProject,
  createProject,
  updateProject,
  updateProjectStatus,
  updateProjectPO,
  deleteProject,
  getProjectBudgetStatus,
  getProjectsWithBudgetAlerts,
  getProjectsSummary,
  exportProjectsToCSV,
  calculateBudgetMetrics,
  getBudgetTotalsByProject,
};

export default projectService;
